import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Badge, Spinner, Alert, Button, Modal, Form, Image } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { config, authService, reservationService } from '../services/api';
import './MyReservations.css';

function MyReservations() {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [paymentProof, setPaymentProof] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [viewProofModal, setViewProofModal] = useState(false);
  const [proofToView, setProofToView] = useState(null);

  useEffect(() => {
    const fetchReservations = async () => {
      try {
        // Use the dedicated user reservations endpoint
        const response = await axios.get(`${config.API_URL}/api/reservations/user/`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        setReservations(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching reservations:', error);
        setError('Impossible de charger vos réservations. Veuillez réessayer plus tard.');
        setLoading(false);
        
        // If unauthorized, redirect to login
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          alert('Votre session a expiré. Veuillez vous reconnecter.');
          authService.logout();
          window.location.href = '/login';
        }
      }
    };

    fetchReservations();
  }, []);

  const getStatusBadge = (status) => {
    const statusMap = {
      'pending': { variant: 'warning', text: 'En attente' },
      'payment_submitted': { variant: 'warning', text: 'Paiement envoyé' },
      'confirmed': { variant: 'success', text: 'Confirmée' },
      'cancelled': { variant: 'danger', text: 'Annulée' },
      'completed': { variant: 'info', text: 'Terminée' }
    };

    const defaultStatus = { variant: 'secondary', text: 'Inconnu' };
    const statusInfo = statusMap[status] || defaultStatus;

    return (
      <Badge bg={statusInfo.variant} className="fs-6">
        {statusInfo.text}
      </Badge>
    );
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('payment_proof', paymentProof);

      await axios.patch(
        `${config.API_URL}/api/reservations/${selectedReservation.id}/update_payment_proof/`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      // Refresh reservations list
      const response = await axios.get(`${config.API_URL}/api/reservations/user/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setReservations(response.data);
      
      setShowModal(false);
      setPaymentProof(null);
      setSelectedReservation(null);
      
      // Show success message
      alert('Preuve de paiement envoyée avec succès!');
    } catch (error) {
      console.error('Error submitting payment:', error);
      // More specific error message
      const errorMessage = error.response?.data?.error || 
        'Erreur lors de l\'envoi du paiement. Veuillez réessayer.';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const viewPaymentProof = (proofUrl) => {
    setProofToView(proofUrl);
    setViewProofModal(true);
  };

  if (loading) {
    return (
      <Container className="py-5">
        <div className="text-center my-5">
          <Spinner animation="border" variant="danger" />
          <p className="mt-3">Chargement de vos réservations...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          <i className="fas fa-exclamation-circle me-2"></i>
          {error}
        </Alert>
      </Container>
    );
  }

  if (reservations.length === 0) {
    return (
      <Container className="py-5 mt-5">
        <Card className="text-center p-5">
          <Card.Body>
            <i className="fas fa-calendar-times fa-3x text-muted mb-3"></i>
            <h3>Aucune réservation</h3>
            <p className="text-muted">Vous n'avez pas encore de réservations.</p>
            <Link to="/evenements" className="btn btn-danger">
              <i className="fas fa-calendar-alt me-2"></i>
              Découvrir les événements
            </Link>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container fluid className="py-5 mt-5">
      <Row className="mb-4 align-items-center">
        <Col>
          <h2 className="mb-0">Mes Réservations</h2>
        </Col>
      </Row>

      {/* Data Grid for Desktop */}
      <div className="bg-white rounded shadow-sm d-none d-md-block">
        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead className="bg-light">
              <tr>
                <th>Événement</th>
                <th>Date & Heure</th>
                <th>Lieu</th>
                <th>Places</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {reservations.map((reservation) => (
                <tr key={reservation.id}>
                  <td className="text-nowrap">
                    <Link 
                      to={`/event/${reservation.event.id}`}
                      className="text-decoration-none text-dark"
                    >
                      <i className="fas fa-calendar-alt me-2 text-muted"></i>
                      {reservation.event.title}
                    </Link>
                  </td>
                  <td className="text-nowrap">
                    <div>{reservation.event.formatted_date}</div>
                    <small className="text-muted">{reservation.event.time}</small>
                  </td>
                  <td className="text-nowrap">
                    <i className="fas fa-map-marker-alt me-2 text-muted"></i>
                    {reservation.event.wilaya} - {reservation.event.wilaya_name}
                  </td>
                  <td>
                    <span className="badge bg-secondary">
                      <i className="fas fa-users me-1"></i>
                      {reservation.reserved_seats}
                    </span>
                  </td>
                  <td>{getStatusBadge(reservation.status)}</td>
                  <td>
                    <div className="d-flex gap-2">
                      <Link 
                        to={`/event/${reservation.event.id}`}
                        className="btn btn-outline-secondary btn-sm"
                        title="Voir les détails"
                      >
                        <i className="fas fa-info-circle"></i>
                      </Link>
                      
                      {reservation.status === 'pending' && (
                        <Button
                          variant="primary"
                          size="sm"
                          title="Confirmer le paiement"
                          onClick={() => {
                            setSelectedReservation(reservation);
                            setShowModal(true);
                          }}
                        >
                          <i className="fas fa-receipt"></i>
                        </Button>
                      )}
                      
                      {reservation.payment_proof && (
                        <Button
                          variant="info"
                          size="sm"
                          title="Voir la preuve de paiement"
                          onClick={() => viewPaymentProof(reservation.payment_proof)}
                        >
                          <i className="fas fa-eye"></i>
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Cards for Mobile */}
      <div className="d-md-none">
        {reservations.map((reservation) => (
          <Card key={reservation.id} className="mb-3 shadow-sm">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div className="text-truncate">
                <i className="fas fa-calendar-alt me-2"></i>
                {reservation.event.title}
              </div>
              {getStatusBadge(reservation.status)}
            </Card.Header>
            <Card.Body>
              <div className="mb-3">
                <div className="text-muted mb-2">
                  <i className="far fa-clock me-2"></i>
                  {reservation.event.formatted_date}
                  <div className="small">{reservation.event.time}</div>
                </div>
                <div className="text-muted mb-2">
                  <i className="fas fa-map-marker-alt me-2"></i>
                  {reservation.event.wilaya} - {reservation.event.wilaya_name}
                </div>
                <div className="text-muted">
                  <i className="fas fa-users me-2"></i>
                  {reservation.reserved_seats} place(s)
                </div>
              </div>

              <div className="d-flex gap-2">
                <Link 
                  to={`/event/${reservation.event.id}`}
                  className="btn btn-outline-secondary"
                >
                  <i className="fas fa-info-circle me-2"></i>
                  Détails
                </Link>
                
                {reservation.status === 'pending' && (
                  <Button
                    variant="primary"
                    onClick={() => {
                      setSelectedReservation(reservation);
                      setShowModal(true);
                    }}
                  >
                    <i className="fas fa-receipt me-2"></i>
                    Paiement
                  </Button>
                )}
                
                {reservation.payment_proof && (
                  <Button
                    variant="info"
                    onClick={() => viewPaymentProof(reservation.payment_proof)}
                  >
                    <i className="fas fa-eye me-2"></i>
                    Preuve
                  </Button>
                )}
              </div>
            </Card.Body>
          </Card>
        ))}
      </div>

      {/* Payment Confirmation Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirmation de Paiement</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedReservation && (
            <div className="mb-3">
              <h6>{selectedReservation.event.title}</h6>
              <p className="text-muted small mb-3">
                {selectedReservation.event.formatted_date} - {selectedReservation.reserved_seats} places
              </p>
            </div>
          )}
          <Form onSubmit={handlePaymentSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Preuve de paiement</Form.Label>
              <Form.Control
                type="file"
                onChange={(e) => setPaymentProof(e.target.files[0])}
                required
                accept="image/*,.pdf"
              />
              <Form.Text className="text-muted">
                Formats acceptés: Images (JPG, PNG) ou PDF
              </Form.Text>
            </Form.Group>
            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={() => setShowModal(false)}>
                Annuler
              </Button>
              <Button 
                variant="primary" 
                type="submit"
                disabled={!paymentProof || submitting}
              >
                {submitting ? 'Envoi en cours...' : 'Envoyer'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Payment Proof Viewer Modal */}
      <Modal 
        show={viewProofModal} 
        onHide={() => setViewProofModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Preuve de Paiement</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {proofToView && (
            proofToView.toLowerCase().endsWith('.pdf') ? (
              <div className="pdf-container">
                <iframe
                  src={proofToView}
                  title="Payment Proof PDF"
                  width="100%"
                  height="500px"
                  className="pdf-viewer"
                />
              </div>
            ) : (
              <Image 
                src={proofToView} 
                alt="Preuve de paiement" 
                fluid 
                className="payment-proof-image"
              />
            )
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setViewProofModal(false)}>
            Fermer
          </Button>
          <Button 
            variant="primary" 
            href={proofToView} 
            target="_blank" 
            rel="noopener noreferrer"
          >
            <i className="fas fa-download me-1"></i>
            Télécharger
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
);
}

export default MyReservations;

// Add this helper function near the top of the component
const formatPrice = (price) => {
  if (!price) return 'Gratuit';
  // Remove decimal places and add thousands separator
  return parseInt(price).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ") + ' DZD';
};

// In the table view, update the price display if it exists
// This would be in the data grid section where prices are shown